import { useActionTimeout } from '@/features/actions/logic/useActionTimeout'
import { DialogContentSkeleton } from '@/features/dialogs/common/components/skeletons/DialogContentSkeleton'
import { withSuspense } from '@/ui/utils/withSuspense'
import { useCallback } from 'react'
import { Address } from 'viem'
import { CollateralStatusSuccessView } from './components/CollateralStatusSuccessView'
import { useECollateralStatusDialog } from './logic/useECollateralStatusDialog'
import { ECollateralStatusView } from './views/ECollateralStatusView'

interface ECollateralStatusDialogContentContainerProps {
  closeDialog: () => void
  collateralVault: Address
  collateralSymbol: string
  collateralDecimals: number
  collateralAsset: Address
  collateralLogoURI?: string
  currentCollateralFormatted: number
  collateralPriceUSD: number
  subAccountId: number
  evcAddress: Address
  action: 'enable' | 'disable'
  currentDebtUSD: number
  accountTotalCollateralUSD: number
  liquidationLTV: number
  currentLTV: number
  mode?: 'collateral' | 'controller'
}

function ECollateralStatusDialogContentContainer({
  closeDialog,
  collateralVault,
  collateralSymbol,
  collateralDecimals,
  collateralAsset,
  collateralLogoURI,
  currentCollateralFormatted,
  collateralPriceUSD,
  subAccountId,
  evcAddress,
  action,
  currentDebtUSD,
  accountTotalCollateralUSD,
  liquidationLTV,
  currentLTV,
  mode = 'collateral',
}: ECollateralStatusDialogContentContainerProps) {
  const {
    collateralToken,
    pageStatus,
    isSimulating,
    simulationSuccess,
    error,
    errorMessage,
    canExecute,
    isLoading,
    handleConfirm,
    collateralValueUSD,
    currentHealthFactor,
    newHealthFactor,
    newLTV,
    isSimulatingHealthFactor,
  } = useECollateralStatusDialog({
    collateralVault,
    collateralSymbol,
    collateralDecimals,
    collateralAsset,
    collateralLogoURI,
    currentCollateralFormatted,
    collateralPriceUSD,
    subAccountId,
    evcAddress,
    action,
    currentDebtUSD,
    accountTotalCollateralUSD,
    liquidationLTV,
  })

  const collateralTimeout = useActionTimeout({
    timeoutMs: 15000,
    isLoading: isLoading,
    isSuccess: pageStatus.state === 'success',
    error: error,
  })

  const handleConfirmWithTimeout = useCallback(() => {
    collateralTimeout.handleActionClick(handleConfirm)
  }, [collateralTimeout, handleConfirm])

  if (pageStatus.state === 'success' && collateralToken) {
    return <CollateralStatusSuccessView token={collateralToken} action={action} onProceed={closeDialog} mode={mode} />
  }

  return (
    <ECollateralStatusView
      collateralSymbol={collateralSymbol}
      collateralLogoURI={collateralLogoURI}
      collateralAsset={collateralAsset}
      collateralDecimals={collateralDecimals}
      currentCollateralFormatted={currentCollateralFormatted}
      collateralValueUSD={collateralValueUSD}
      action={action}
      isSimulating={isSimulating}
      simulationSuccess={simulationSuccess}
      error={error}
      errorMessage={errorMessage}
      canExecute={canExecute}
      isLoading={isLoading}
      onConfirm={handleConfirmWithTimeout}
      currentHealthFactor={currentHealthFactor}
      newHealthFactor={newHealthFactor}
      currentLTV={currentLTV}
      newLTV={newLTV}
      isSimulatingHealthFactor={isSimulatingHealthFactor}
      mode={mode}
      isTimedOut={collateralTimeout.isTimedOut}
      onRetry={handleConfirmWithTimeout}
      isRetryDisabled={isLoading || isSimulating}
    />
  )
}

const ECollateralStatusDialogContentContainerWithSuspense = withSuspense(
  ECollateralStatusDialogContentContainer,
  DialogContentSkeleton,
)

export { ECollateralStatusDialogContentContainerWithSuspense as ECollateralStatusDialogContentContainer }
